import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import CosmicBackground from './components/CosmicBackground';
import FloatingProjectCard from './components/FloatingProjectCard';
import CentralNexus from './components/CentralNexus';
import CreatorIntro from './components/CreatorIntro';
import NavigationOverlay from './components/NavigationOverlay';
import PerformanceMonitor from './components/PerformanceMonitor';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const Homepage3DHub = () => {
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(true);
  const [showNavOverlay, setShowNavOverlay] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  const [quality, setQuality] = useState('high');
  const [isLoaded, setIsLoaded] = useState(false);

  // Mock project data
  const projects = [
    {
      id: 1,
      title: "Immersive E-commerce Platform",
      description: "3D product visualization with AR integration for enhanced shopping experience",
      category: "Web Design",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop",
      technologies: ["React", "Three.js", "WebXR", "Node.js", "MongoDB"],
      route: "/interactive-portfolio-gallery"
    },
    {
      id: 2,
      title: "Interactive Data Visualization",
      description: "Real-time 3D charts and graphs for complex data analysis",
      category: "Creative Coding",
      image: "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?w=400&h=300&fit=crop",
      technologies: ["D3.js", "WebGL", "Python", "FastAPI", "PostgreSQL"],
      route: "/interactive-portfolio-gallery"
    },
    {
      id: 3,
      title: "Brand Experience Portal",
      description: "Immersive storytelling platform for luxury brand presentation",
      category: "Brand Experience",
      image: "https://images.pixabay.com/photo/2016/11/29/06/15/plans-1867745_1280.jpg?w=400&h=300&fit=crop",
      technologies: ["Next.js", "Framer Motion", "GSAP", "Strapi", "AWS"],
      route: "/interactive-portfolio-gallery"
    },
    {
      id: 4,
      title: "Virtual Art Gallery",
      description: "3D gallery space for digital art exhibitions and NFT showcases",
      category: "Interactive Installation",
      image: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=300&fit=crop",
      technologies: ["Babylon.js", "Ethereum", "IPFS", "React", "Express"],
      route: "/interactive-portfolio-gallery"
    },
    {
      id: 5,
      title: "Spatial Audio Experience",
      description: "3D audio visualization with interactive sound manipulation",
      category: "Creative Coding",
      image: "https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?w=400&h=300&fit=crop",
      technologies: ["Web Audio API", "Three.js", "WebRTC", "Socket.io", "Redis"],
      route: "/interactive-portfolio-gallery"
    },
    {
      id: 6,
      title: "Architectural Visualization",
      description: "Interactive 3D building tours with real-time customization",
      category: "Web Design",
      image: "https://images.pixabay.com/photo/2017/07/09/03/19/home-2486092_1280.jpg?w=400&h=300&fit=crop",
      technologies: ["Unity WebGL", "Blender", "React", "Firebase", "Stripe"],
      route: "/interactive-portfolio-gallery"
    }
  ];

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Keyboard navigation
    const handleKeyPress = (e) => {
      if (e?.key === 'Escape') {
        setShowNavOverlay(false);
      } else if (e?.key === 'n' || e?.key === 'N') {
        setShowNavOverlay(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const handleIntroComplete = () => {
    setShowIntro(false);
  };

  const handleProjectHover = (project) => {
    setActiveProject(project);
  };

  const handleProjectClick = (project) => {
    navigate(project?.route);
  };

  const handleNavigation = (route) => {
    navigate(route);
  };

  const handleQualityChange = (newQuality) => {
    setQuality(newQuality);
  };

  return (
    <>
      <Helmet>
        <title>3D Creative Universe - Amit Pandey | Interactive Portfolio</title>
        <meta name="description" content="Welcome to Alex Chen's immersive 3D portfolio showcase. Explore interactive projects, creative coding experiments, and innovative web experiences." />
        <meta name="keywords" content="3D portfolio, creative developer, interactive design, Three.js, WebGL, creative coding" />
        <meta property="og:title" content="3D Creative Universe - Interactive Portfolio" />
        <meta property="og:description" content="Immersive 3D portfolio showcasing innovative web experiences and creative technology projects." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-background overflow-hidden relative">
        <Header />
        
        {/* Cosmic Background */}
        <CosmicBackground />

        {/* Loading Screen */}
        {!isLoaded && (
          <div className="fixed inset-0 z-60 bg-background flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-accent/30 border-t-accent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="font-body text-muted-foreground">Loading 3D Universe...</p>
            </div>
          </div>
        )}

        {/* Creator Introduction */}
        {isLoaded && (
          <CreatorIntro 
            isVisible={showIntro} 
            onComplete={handleIntroComplete} 
          />
        )}

        {/* Main 3D Scene */}
        {isLoaded && !showIntro && (
          <div className="relative h-screen">
            {/* Central Nexus */}
            <CentralNexus 
              activeProject={activeProject}
              onNavigate={handleNavigation}
            />

            {/* Floating Project Cards */}
            <div className="absolute inset-0">
              {projects?.map((project, index) => (
                <FloatingProjectCard
                  key={project?.id}
                  project={project}
                  index={index}
                  isActive={activeProject?.id === project?.id}
                  onHover={handleProjectHover}
                  onClick={handleProjectClick}
                />
              ))}
            </div>

            {/* UI Controls */}
            <div className="absolute top-24 left-6 z-30 space-y-4">
              <Button
                variant="outline"
                onClick={() => setShowNavOverlay(true)}
                iconName="Menu"
                className="glass-panel border-accent/30 text-accent hover:bg-accent/10"
              >
                Navigation
              </Button>
              
              <div className="glass-panel rounded-lg p-3 text-center">
                <Icon name="MousePointer" size={20} className="text-accent mx-auto mb-2" />
                <p className="font-body text-xs text-muted-foreground">
                  Drag to explore
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="absolute top-24 right-6 z-30 space-y-4">
              <Button
                variant="outline"
                onClick={() => navigate('/contact-nexus')}
                iconName="MessageCircle"
                className="glass-panel border-cosmic-cta/30 text-cosmic-cta hover:bg-cosmic-cta/10"
              >
                Let's Talk
              </Button>
            </div>

            {/* Project Counter */}
            <div className="absolute bottom-6 left-6 z-30">
              <div className="glass-panel rounded-lg px-4 py-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Briefcase" size={16} className="text-accent" />
                  <span className="font-body text-sm text-foreground">
                    {projects?.length} Projects
                  </span>
                </div>
              </div>
            </div>

            {/* Keyboard Shortcuts */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30">
              <div className="glass-panel rounded-lg px-4 py-2">
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <kbd className="px-2 py-1 bg-surface/50 rounded text-xs">N</kbd>
                    <span>Navigation</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <kbd className="px-2 py-1 bg-surface/50 rounded text-xs">ESC</kbd>
                    <span>Close</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Overlay */}
        <NavigationOverlay
          isVisible={showNavOverlay}
          onToggle={() => setShowNavOverlay(!showNavOverlay)}
        />

        {/* Performance Monitor */}
        {isLoaded && !showIntro && (
          <PerformanceMonitor onQualityChange={handleQualityChange} />
        )}

        {/* Ambient Particles */}
        {isLoaded && !showIntro && quality !== 'low' && (
          <div className="fixed inset-0 pointer-events-none z-10">
            {[...Array(quality === 'high' ? 50 : 25)]?.map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-accent/30 rounded-full animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 10}s`,
                  animationDuration: `${8 + Math.random() * 8}s`
                }}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default Homepage3DHub;